import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";

export const Navbar = () => {
  const { t } = useLang();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/projektas", label: t("nav.about") },
    { to: "/migdolas", label: t("nav.migdolas") },
    { to: "/vysnia", label: t("nav.vysnia") },
    { to: "/vieta", label: t("nav.location") },
    { to: "/galerija", label: t("nav.gallery") },
    { to: "/kontaktai", label: t("nav.contact") },
  ];

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-charcoal/95 backdrop-blur-md border-b border-gold/15 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 leading-none">
          <img src="/logo.png" alt="Sakurų namai" className="w-11 h-11 rounded-full object-cover border border-gold/40 bg-white" />
          <span className="brand-logo text-2xl text-cream tracking-wide">Sakurų namai</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={`text-xs uppercase tracking-widest font-sans transition-colors duration-300 ${
                isActive(l.to) ? "text-gold" : "text-cream/70 hover:text-gold"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <LanguageSwitcher />
          <Link
            to="/admin"
            data-testid="nav-admin"
            className="text-[10px] uppercase tracking-widest text-cream/40 hover:text-gold border border-cream/15 hover:border-gold/40 rounded-full px-3 py-1 transition-colors"
          >
            {t("nav.adminLogin")}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-cream"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-charcoal/98 backdrop-blur-md border-t border-gold/15 mt-3" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-4 gap-4">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={`text-sm uppercase tracking-widest ${isActive(l.to) ? "text-gold" : "text-cream/80"}`}
              >
                {l.label}
              </Link>
            ))}
            <div className="flex items-center justify-between pt-3 border-t border-cream/10">
              <LanguageSwitcher />
              <Link to="/admin" className="text-[10px] uppercase tracking-widest text-cream/50 border border-cream/15 rounded-full px-3 py-1">
                {t("nav.adminLogin")}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
